import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { NewGamePayload, DefaultService } from '../../../generated-api';

interface NewGameCardProps {
  payload: NewGamePayload;
  createdAt: string;
}

export const NewGameCard: React.FC<NewGameCardProps> = ({ payload, createdAt }) => {
  const [gameImage, setGameImage] = useState<string | null>(payload.gameImage || null);

  // Fetch game image if not in payload
  useEffect(() => {
    if (!gameImage) {
      const fetchGameImage = async () => {
        try {
          const game = await DefaultService.getGameGamesGameIdGet({ gameId: payload.gameId });
          if (game.imageUrl) {
            setGameImage(game.imageUrl);
          }
        } catch (err) {
          // Silently fail - image is not critical
          console.error('Failed to fetch game image:', err);
        }
      };
      fetchGameImage();
    }
  }, [payload.gameId, gameImage]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const hasGameImage = gameImage && gameImage.trim() !== '';

  return (
    <Link
      data-track="feed_new_game_card"
      to={`/games/${payload.gameId}`}
      className="block bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-purple-100 rounded-full p-2">
              <PuzzlePieceIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">New Game Added</p>
              <p className="text-xs text-muted">{formatRelativeTime(createdAt)}</p>
            </div>
          </div>
          {hasGameImage && (
            <img
              src={gameImage!}
              alt={payload.gameName}
              className="h-32 max-w-40 rounded object-contain flex-shrink-0"
              onError={(e) => {
                console.error('Failed to load game image:', gameImage);
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            <span className="font-medium text-purple-600">{payload.gameName}</span>
            {' was just added to the game library.'}
          </p>
        </div>
      </div>
    </Link>
  );
};
